import { CONSENT_STORAGE_KEY, CONSENT_UPDATED_EVENT, readConsentChoice } from './analytics';

const SESSION_KEY = 'olgun_traffic_session_v1';
const ATTRIBUTION_KEY = 'olgun_traffic_attribution_v1';
const ENDPOINT = '/api/traffic/events';
const PARAMS = {
  gclid: 'gclid', gbraid: 'gbraid', wbraid: 'wbraid', campaign_id: 'campaign_id',
  adgroup_id: 'adgroup_id', creative_id: 'creative_id', keyword: 'keyword', device: 'device',
  utm_source: 'utm_source', utm_medium: 'utm_medium'
};

export function createTrafficClient({ enabled, browser, readConsent = readConsentChoice }) {
  let session = null;
  let lastPage = null;
  const timers = new Set();
  const consent = () => {
    const choice = readConsent();
    return { analytics: choice?.analytics_storage === 'granted', advertising: choice?.ad_storage === 'granted' };
  };
  const remove = key => { try { browser.sessionStorage.removeItem(key); } catch {} };
  const reset = () => {
    session = null;
    lastPage = null;
    remove(SESSION_KEY);
    remove(ATTRIBUTION_KEY);
    for (const timer of timers) browser.clearTimeout(timer);
    timers.clear();
  };
  const sessionId = () => {
    if (session) return session;
    try {
      const existing = browser.sessionStorage.getItem(SESSION_KEY);
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(existing || '')) session = existing;
    } catch {}
    session = session || browser.crypto.randomUUID();
    try { browser.sessionStorage.setItem(SESSION_KEY, session); } catch {}
    return session;
  };
  const attribution = () => {
    if (!consent().advertising) { remove(ATTRIBUTION_KEY); return {}; }
    const params = new URLSearchParams(browser.location.search);
    let result = {};
    try { result = JSON.parse(browser.sessionStorage.getItem(ATTRIBUTION_KEY) || '{}'); } catch {}
    if (!result || typeof result !== 'object' || Array.isArray(result)) result = {};
    if (['gclid', 'gbraid', 'wbraid'].some(key => params.has(key))) result = {};
    for (const [key, param] of Object.entries(PARAMS)) {
      const value = params.get(param);
      if (value) result[key] = Array.from(value).filter(char => char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127).join('').slice(0, key === 'keyword' ? 200 : 160);
    }
    // Support the existing campaign suffix until it is replaced with explicit IDs.
    if (/^\d+$/.test(params.get('utm_campaign') || '')) result.campaign_id = params.get('utm_campaign').slice(0, 160);
    if (/^\d+$/.test(params.get('utm_content') || '')) result.adgroup_id = params.get('utm_content').slice(0, 160);
    try { browser.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(result)); } catch {}
    return result;
  };
  const track = event => {
    if (!enabled || !browser || !consent().analytics || !browser.crypto?.randomUUID) return;
    const payload = {
      event_id: browser.crypto.randomUUID(), session_id: sessionId(), event,
      path: browser.location.pathname, consent: consent(), attribution: attribution()
    };
    const send = attempt => {
      if (!consent().analytics) return;
      if (!consent().advertising) { payload.consent.advertising = false; payload.attribution = {}; }
      browser.fetch(ENDPOINT, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', keepalive: true, body: JSON.stringify(payload)
      }).then(response => {
        if (response.status >= 500 && attempt === 0) retry();
      }).catch(() => { if (attempt === 0) retry(); });
    };
    const retry = () => {
      if (!consent().analytics) return;
      const timer = browser.setTimeout(() => { timers.delete(timer); send(1); }, 1000);
      timers.add(timer);
    };
    send(0);
  };
  const pageView = () => {
    if (!enabled || !browser || !consent().analytics) return;
    const key = browser.location.pathname + browser.location.search;
    if (key === lastPage) return;
    lastPage = key;
    track('page_view');
  };
  const install = () => {
    if (!enabled || !browser) return () => {};
    let previousAnalytics = consent().analytics;
    const changed = event => {
      if (event?.type === 'storage' && event.key !== CONSENT_STORAGE_KEY && event.key !== null) return;
      if (!consent().analytics) reset();
      else {
        if (!consent().advertising) remove(ATTRIBUTION_KEY);
        if (!previousAnalytics) { lastPage = null; pageView(); }
      }
      previousAnalytics = consent().analytics;
    };
    browser.addEventListener(CONSENT_UPDATED_EVENT, changed);
    browser.addEventListener('storage', changed);
    return () => {
      browser.removeEventListener(CONSENT_UPDATED_EVENT, changed);
      browser.removeEventListener('storage', changed);
      for (const timer of timers) browser.clearTimeout(timer);
      timers.clear();
    };
  };
  return { track, pageView, install };
}

const trafficClient = createTrafficClient({
  enabled: process.env.REACT_APP_TRAFFIC_ENABLED === 'true',
  browser: typeof window === 'undefined' ? null : window
});
export default trafficClient;
